/**
 * Custom hook for popups.
 * @module hooks/usePopups
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPopups } from '../actions/popup';

interface PopupData {
  '@id': string;
  '@type': string;
  title: string;
  description?: string;
  image?: {
    download: string;
    content_type: string;
    filename: string;
  };
  link_url?: string;
  link_text?: string;
  start_date: string;
  end_date: string;
  active: boolean;
  priority: number;
}

/**
 * Custom hook for popups.
 * @function usePopups
 * @param {Object} options Search options.
 * @returns {Object} Popup state and actions.
 */
export const usePopups = (options = {}) => {
  const dispatch = useDispatch();

  // Memoize options to prevent unnecessary re-renders
  const memoizedOptions = useMemo(() => options, [JSON.stringify(options)]);

  // Use Volto's standard search reducer with subrequest
  const searchState = useSelector(
    (state: any) => state.search.subrequests?.popups,
  );
  const { items, loading, loaded, error } = searchState || {};

  // Filter active popups by date range
  const activePopups = useMemo(() => {
    if (!items || items.length === 0) return [];

    const now = new Date();
    return items.filter((popup: PopupData) => {
      const startDate = new Date(popup.start_date);
      const endDate = new Date(popup.end_date);
      return now >= startDate && now <= endDate;
    });
  }, [items]);

  // Get the highest priority popup
  const currentPopup = useMemo(() => {
    if (activePopups.length === 0) return null;

    // Sort by priority (1 = highest priority)
    const sortedPopups = [...activePopups].sort(
      (a, b) => a.priority - b.priority,
    );
    return sortedPopups[0];
  }, [activePopups]);

  // Memoize the refetch function to prevent unnecessary re-renders
  const refetch = useCallback(() => {
    dispatch(getPopups(memoizedOptions));
  }, [dispatch, memoizedOptions]);

  // Fetch popups on mount and when options change
  useEffect(() => {
    dispatch(getPopups(memoizedOptions));
  }, [dispatch, memoizedOptions]);

  return {
    popups: activePopups,
    currentPopup,
    loading: loading || false,
    loaded: loaded || false,
    error: error || null,
    refetch,
  };
};
